import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Copy,
  Download,
  Pencil,
  Plus,
  Terminal,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

import { SlashCommandFormFields } from './SlashCommandFormFields';
import { buildShareUrl, decodeSharePayload } from './slashCommandUtils';
import { SlashCommand, SlashCommandSharePayload } from './types';

interface SlashCommandManageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zIndex?: number;
  commands: SlashCommand[];
  onAdd: (data: {
    name: string;
    description: string;
    promptText: string;
  }) => Promise<SlashCommand | string>;
  onUpdate: (
    id: string,
    data: Partial<Pick<SlashCommand, 'name' | 'description' | 'promptText'>>,
  ) => Promise<string | null>;
  onDelete: (id: string) => Promise<void>;
  onImport: (commands: SlashCommandSharePayload['commands']) => Promise<number>;
}

type ViewState =
  | { mode: 'list' }
  | { mode: 'create' }
  | { mode: 'edit'; command: SlashCommand }
  | { mode: 'import' };

export const SlashCommandManageDialog = ({
  open,
  onOpenChange,
  zIndex,
  commands,
  onAdd,
  onUpdate,
  onDelete,
  onImport,
}: SlashCommandManageDialogProps) => {
  const [view, setView] = useState<ViewState>({ mode: 'list' });
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [importUrl, setImportUrl] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setView({ mode: 'list' });
      setDeletingId(null);
      setImportUrl('');
      setImportError(null);
    }
    onOpenChange(isOpen);
  };

  const handleCreate = async (data: {
    name: string;
    description: string;
    promptText: string;
  }) => {
    const result = await onAdd(data);
    if (typeof result === 'string') {
      toast.error(result);
      return;
    }
    toast.success(`Created /${data.name}`);
    setView({ mode: 'list' });
  };

  const handleUpdate = async (
    id: string,
    data: { name: string; description: string; promptText: string },
  ) => {
    const error = await onUpdate(id, data);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(`Updated /${data.name}`);
    setView({ mode: 'list' });
  };

  const handleDelete = async (id: string) => {
    const cmd = commands.find((c) => c.id === id);
    try {
      await onDelete(id);
      if (cmd) toast.success(`Deleted /${cmd.name}`);
    } catch {
      toast.error('Failed to delete command');
    }
    setDeletingId(null);
  };

  const handleShareAll = async () => {
    if (commands.length === 0) {
      toast.error('No commands to share');
      return;
    }
    const url = buildShareUrl(commands);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleImportFromUrl = async () => {
    setImportError(null);
    let url: URL;
    try {
      url = new URL(importUrl);
    } catch {
      setImportError('Invalid URL');
      return;
    }
    const encoded = url.searchParams.get('slash_commands');
    if (!encoded) {
      setImportError('No slash commands found in this URL');
      return;
    }
    const payload = decodeSharePayload(encoded);
    if (!payload || payload.commands.length === 0) {
      setImportError('Could not decode commands from this URL');
      return;
    }
    try {
      const count = await onImport(payload.commands);
      if (count > 0) {
        toast.success(`Imported ${count} command${count > 1 ? 's' : ''}`);
      } else {
        toast.info('All commands already exist');
      }
      setImportUrl('');
      setView({ mode: 'list' });
    } catch {
      toast.error('Failed to import commands');
    }
  };

  const existingNames = commands.map((c) => c.name);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" zIndex={zIndex}>
        <DialogHeader>
          <DialogTitle className="text-base font-medium text-gray-900">
            {view.mode === 'list' && 'Manage Slash Commands'}
            {view.mode === 'create' && 'New Command'}
            {view.mode === 'edit' && `Edit /${view.command.name}`}
            {view.mode === 'import' && 'Import Commands'}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {view.mode === 'list' && (
            <motion.div
              key="list"
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 12 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col gap-3"
            >
              {/* Action bar */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setView({ mode: 'create' })}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-900 border border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 rounded transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  New Command
                </button>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={handleShareAll}
                    disabled={commands.length === 0}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Share All
                  </button>
                  <button
                    type="button"
                    onClick={() => setView({ mode: 'import' })}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Import
                  </button>
                </div>
              </div>

              {/* Command list */}
              {commands.length > 0 ? (
                <div className="flex flex-col border border-gray-200 rounded-md divide-y divide-gray-100 max-h-80 overflow-y-auto">
                  <AnimatePresence mode="popLayout">
                    {commands.map((cmd) => (
                      <motion.div
                        key={cmd.id}
                        layout
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        {deletingId === cmd.id ? (
                          <div className="flex items-center justify-between px-3 py-2.5 bg-red-50">
                            <span className="text-sm font-medium text-red-700">
                              Delete /{cmd.name}?
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setDeletingId(null)}
                                className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 rounded transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(cmd.id)}
                                className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between px-3 py-2.5 group">
                            <div className="flex flex-col min-w-0 mr-2">
                              <span className="text-sm font-medium text-gray-900 font-mono leading-tight">
                                /{cmd.name}
                              </span>
                              {cmd.description && (
                                <span className="text-xs text-gray-600 truncate leading-snug">
                                  {cmd.description}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() =>
                                  setView({ mode: 'edit', command: cmd })
                                }
                                className="p-1.5 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-100 transition-colors"
                                aria-label={`Edit /${cmd.name}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeletingId(cmd.id)}
                                className="p-1.5 text-gray-600 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                                aria-label={`Delete /${cmd.name}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-8 border border-gray-200 rounded-md">
                  <Terminal className="h-8 w-8 text-gray-600" />
                  <p className="text-sm font-medium text-gray-900">
                    No slash commands yet
                  </p>
                  <p className="text-xs text-gray-600 text-center max-w-xs">
                    Create reusable prompt templates and access them by typing{' '}
                    <span className="font-mono font-medium">/</span> in the
                    chat input.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {view.mode === 'create' && (
            <motion.div
              key="create"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.15 }}
            >
              <button
                type="button"
                onClick={() => setView({ mode: 'list' })}
                className="flex items-center gap-1 mb-3 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <SlashCommandFormFields
                existingNames={existingNames}
                onSubmit={handleCreate}
                onCancel={() => setView({ mode: 'list' })}
                submitLabel="Create Command"
              />
            </motion.div>
          )}

          {view.mode === 'edit' && (
            <motion.div
              key={`edit-${view.command.id}`}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.15 }}
            >
              <button
                type="button"
                onClick={() => setView({ mode: 'list' })}
                className="flex items-center gap-1 mb-3 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <SlashCommandFormFields
                initialName={view.command.name}
                initialDescription={view.command.description}
                initialPromptText={view.command.promptText}
                existingNames={existingNames}
                onSubmit={(data) => handleUpdate(view.command.id, data)}
                onCancel={() => setView({ mode: 'list' })}
                submitLabel="Save Changes"
              />
            </motion.div>
          )}

          {view.mode === 'import' && (
            <motion.div
              key="import"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col gap-3"
            >
              <button
                type="button"
                onClick={() => {
                  setView({ mode: 'list' });
                  setImportUrl('');
                  setImportError(null);
                }}
                className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <div className="flex flex-col gap-1.5">
                <Label
                  htmlFor="import-url"
                  className="text-sm font-medium text-gray-900"
                >
                  Paste a share link
                </Label>
                <input
                  id="import-url"
                  type="text"
                  value={importUrl}
                  onChange={(e) => {
                    setImportUrl(e.target.value);
                    setImportError(null);
                  }}
                  placeholder="https://..."
                  className="flex h-9 w-full rounded-md border border-gray-200 bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                {importError && (
                  <span className="text-xs font-medium text-red-600">
                    {importError}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setView({ mode: 'list' });
                    setImportUrl('');
                    setImportError(null);
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImportFromUrl}
                  disabled={!importUrl.trim()}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Import
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
